function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function ensureTrailingSlash(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

function isLocalOrigin(origin: string): boolean {
  try {
    const hostname = new URL(origin).hostname.toLowerCase();
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '[::1]';
  } catch {
    return false;
  }
}

export function getApiBaseUrl(): string {
  const explicitFromMeta =
    typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL?.trim();
  const explicitFromEnv = typeof process !== 'undefined' && process.env?.VITE_API_BASE_URL?.trim();
  const explicit = explicitFromMeta || explicitFromEnv;

  if (explicit) {
    return normalizeBaseUrl(explicit);
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = normalizeBaseUrl(window.location.origin);
    if (!isLocalOrigin(origin)) {
      throw new Error('VITE_API_BASE_URL is required when the app is served from a non-local origin.');
    }
    return origin;
  }

  return 'http://localhost:3000';
}

export function getPublicAppBaseUrl(explicitBaseUrl?: string): string {
  if (explicitBaseUrl?.trim()) {
    return normalizeBaseUrl(explicitBaseUrl);
  }

  const publicFromMeta =
    typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_PUBLIC_APP_BASE_URL?.trim();
  const publicFromEnv = typeof process !== 'undefined' && process.env?.VITE_PUBLIC_APP_BASE_URL?.trim();
  const publicBase = publicFromMeta || publicFromEnv;
  if (publicBase) {
    return normalizeBaseUrl(publicBase);
  }

  const legacyFromMeta =
    typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_VIEWER_BASE_URL?.trim();
  const legacyFromEnv = typeof process !== 'undefined' && process.env?.VITE_VIEWER_BASE_URL?.trim();
  const legacyViewerBase = legacyFromMeta || legacyFromEnv;
  if (legacyViewerBase) {
    return normalizeBaseUrl(legacyViewerBase);
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return normalizeBaseUrl(window.location.origin);
  }

  return 'http://localhost:3000';
}

export function buildApiUrl(path: string): string {
  try {
    return new URL(path, ensureTrailingSlash(getApiBaseUrl())).toString();
  } catch {
    // Fallback for test environments where the global URL constructor
    // may be unavailable or restricted — build a safe concatenated URL.
    const base = ensureTrailingSlash(getApiBaseUrl());
    return `${base.replace(/\/+$/,'')}/${path.replace(/^\/+/, '')}`;
  }
}

export function buildPublicAppUrl(path: string, explicitBaseUrl?: string): string {
  try {
    return new URL(path, ensureTrailingSlash(getPublicAppBaseUrl(explicitBaseUrl))).toString();
  } catch {
    const base = ensureTrailingSlash(getPublicAppBaseUrl(explicitBaseUrl));
    return `${base.replace(/\/+$/,'')}/${path.replace(/^\/+/, '')}`;
  }
}
